import { FC, useState } from "react";
import { Box, Flex, Icon, Spinner, Text } from "@storyofams/react-ui";
import { useShopify } from "~context";
import { showToast } from "~lib";
import { User } from "~components/common/Icons";
import { ProductByHandleQuery } from "~lib/shopify/sdk";

interface PriceProps {
  product: ProductByHandleQuery["productByHandle"];
  currentVariant: ProductByHandleQuery["productByHandle"]["variants"]["edges"][0]["node"];
  quantity: number;
}

export const Price: FC<PriceProps> = ({
  currentVariant,
  product,
  quantity,
}) => {
  const {
    countryCode,
    customerCompanyId,
    isB2B,
    updateCustomerType,
    refetchCustomer,
  } = useShopify();
  const [isCustomerUpdating, setIsCustomerUpdating] = useState(false);
  const autoDiscount =
    product?.autoDiscount?.value && product?.autoDiscount?.value !== "0"
      ? 1 - Number(product?.autoDiscount?.value) / 100
      : null;
  const getPrices = () => {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const location = countryCode ? countryCode : locale;
    let collection = product?.collections?.edges?.find(({ node }) =>
      node?.title?.includes(location.toUpperCase())
    );
    if (!collection)
      collection = product?.collections?.edges?.find(({ node }) =>
        node?.title?.includes("NL")
      );
    const tax = 1 - Number(collection?.node.title?.replace(/[^\d]/g, "")) / 100;
    const prices = currentVariant?.quantityPriceBreaks?.nodes.sort(
      (a, b) => +b?.minimumQuantity - +a?.minimumQuantity
    );
    if (customerCompanyId && prices && isB2B) {
      const getPriceByQuantity = () => {
        for (let tier of prices) {
          if (quantity >= tier.minimumQuantity) {
            return tier.price.amount;
          }
        }
        return currentVariant.priceV2.amount;
      };
      const price = getPriceByQuantity();
      return {
        taxPrice: "€" + (price * tax).toFixed(2),
        сompareAt: null,
        price: "€" + (price * 1).toFixed(2),
      };
    } else if (autoDiscount && currentVariant) {
      return {
        taxPrice:
          "€" + (currentVariant.priceV2.amount * tax * autoDiscount).toFixed(2),
        сompareAt: "€" + (currentVariant.priceV2.amount * 1).toFixed(2),
        price: "€" + (currentVariant.priceV2.amount * autoDiscount).toFixed(2),
      };
    } else if (currentVariant?.compareAtPriceV2) {
      return {
        taxPrice: "€" + (currentVariant.priceV2.amount * tax).toFixed(2),
        сompareAt:
          "€" + (currentVariant.compareAtPriceV2.amount * 1).toFixed(2),
        price: "€" + currentVariant.priceV2.amount,
      };
    } else if (currentVariant) {
      return {
        taxPrice: "€" + (currentVariant.priceV2.amount * tax).toFixed(2),
        сompareAt: null,
        price: "€" + (currentVariant.priceV2.amount * 1).toFixed(2),
      };
    } else
      return {
        taxPrice: "€0.00",
        сompareAt: null,
        price: "€0.00",
      };
  };
  const { taxPrice, сompareAt, price } = getPrices();
  const handleSwitch = async () => {
    try {
      setIsCustomerUpdating(true);
      await updateCustomerType();
      refetchCustomer();
    } catch {
      showToast("error", "error");
    } finally {
      setIsCustomerUpdating(false);
    }
  };
  return (
    <Box
      display="flex"
      flexDirection={["column", "row"]}
      justifyContent="space-between"
      alignItems={["start", "end"]}
      mb={[1, 2]}
    >
      <Box width={"max-content"} order={[1, 0]}>
        <Text fontSize={"14px"} color="grey600" mb={1}>
          Prijs
        </Text>
        <Box
          backgroundColor={"#EEE3D7"}
          px={"16px"}
          py={"11.5px"}
          borderRadius={"6px"}
        >
          <Text
            display={"inline"}
            whiteSpace={"nowrap"}
            fontWeight={600}
            lineHeight={"140%"}
          >
            {сompareAt && (
              <Text
                opacity={"40%"}
                display={"inline"}
                textDecoration={"line-through"}
                mr="3px"
              >
                {сompareAt}
              </Text>
            )}
            {`${price} incl. btw`}
          </Text>
          <Box mt="2px" />
          <Text
            display={"inline"}
            fontSize={"14px"}
            lineHeight={"140%"}
            whiteSpace={"nowrap"}
            color="#1D1D1BCC"
          >
            {`${taxPrice} excl. btw`}
          </Text>
        </Box>
      </Box>
      {customerCompanyId && (
        <Box
          order={[0, 1]}
          py={1}
          mb={[2, 0]}
          height={"max-content"}
          px={2}
          borderRadius={"8px"}
          backgroundColor={"#FCF9F7"}
          lineHeight={"22px"}
          textAlign={["start", "end"]}
          width={["100%", "max-content"]}
        >
          {isCustomerUpdating ? (
            <Spinner my={2} />
          ) : (
            <>
              <Flex mb={1}>
                <Icon mr={1} fontSize={2} icon={User}></Icon>
                <Text>
                  {isB2B
                    ? "Je bent ingelogd als groothandel klant"
                    : "Je bent ingelogd als consument"}
                </Text>
              </Flex>
              <Text
                cursor={"pointer"}
                textDecoration={"underline"}
                fontWeight={600}
                fontSize={2}
                lineHeight={"140%"}
                onClick={handleSwitch}
              >
                {isB2B
                  ? "Schakel over naar jouw consument account"
                  : "Schakel over naar jouw groothandel account"}
              </Text>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
